import React, { forwardRef, useEffect, useRef, useState } from "react";
import Box, { BoxProps } from "../box/Box";
import { getSecureContent } from "../anchor";

interface IImage {
  /**
   * The path to the image source
   */
  src?: string;
  /**
   * In event there was an error loading the `src`, specify a fallback
   * In most cases, this can be an avatar or image placeholder
   */
  fallbackSrc?: string;
  /**
   * The alt text that describes the image
   */
  alt: string;
  /**
   * A callback for when the image `src` has been loaded
   */
  onLoad?: (e?: any) => void;
  /**
   * A callback for when there was an error loading the image `src`
   */
  onError?: (e?: any) => void;
  /**
   * The native HTML `width` attribute to the passed to the `img`
   */
  htmlWidth?: string | number;
  /**
   * The native HTML `height` attribute to the passed to the `img`
   */
  htmlHeight?: string | number;
  /**
   * Opt out of the `fallbackSrc` logic and use the `Image` directly
   */
  ignoreFallback?: boolean;

  external?: "outside-govlaunch" | "govlaunch" | "outside-redirect";
}

export type ImageProps = IImage & BoxProps;

type TImageHasLoadedProps = Pick<IImage, "src" | "onLoad" | "onError">;

export const useHasImageLoaded = ({ src, onLoad, onError }: TImageHasLoadedProps) => {
  const isMounted = useRef(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!src) {
      return;
    }
    const image = new window.Image();
    image.src = src;

    image.onload = event => {
      if (isMounted.current) {
        setHasLoaded(true);
        onLoad && onLoad(event);
      }
    };

    image.onerror = event => {
      if (isMounted.current) {
        setHasLoaded(false);
        onError && onError(event);
      }
    };
  }, [src, onLoad, onError]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return hasLoaded;
};

const NativeImage = forwardRef<HTMLImageElement, IImage>(({ htmlWidth, htmlHeight, alt, ...props }, ref) => (
  <img width={htmlWidth} height={htmlHeight} ref={ref} alt={alt} {...props} />
));

NativeImage.displayName = "NativeImage";

export const Image: React.FC<ImageProps> = forwardRef<any, ImageProps>(
  ({ src, fallbackSrc, onError, onLoad, external = "govlaunch", ignoreFallback, ...props }, ref) => {
    const finalSrc = getSecureContent(src, external);

    const hasLoaded = useHasImageLoaded({
      src: finalSrc,
      onLoad,
      onError,
    });
    let imageProps;
    if (ignoreFallback) {
      imageProps = { src: finalSrc, onLoad, onError };
    } else {
      imageProps = { src: hasLoaded ? finalSrc : fallbackSrc };
    }
    return <Box as={NativeImage} ref={ref} {...imageProps} {...props} />;
  },
);

Image.displayName = "Image";

export default Image;
